/**
 * Every social / contact button a shop owner saved in the dashboard
 * (Shop Dashboard → **Shop Settings → Social Media**: facebook, instagram,
 * twitter, telegram, whatsapp, tiktok, youtube).
 *
 * Used by the community **product page** and the shop view of the community
 * **Shop page**, so a visitor can reach the shop on every platform it uses
 * (the platform names match the fields `Shop.social_media` stores — see
 * `Backend_API/routers/community.py::_shop_card`).
 *
 * `compact` renders icon-only circles (headers / rails); the default renders
 * labelled buttons (the shop card on a product page).
 */
import React from 'react';
import {
  FaFacebook, FaInstagram, FaTiktok, FaTwitter, FaWhatsapp, FaYoutube, FaTelegramPlane,
} from 'react-icons/fa';
import { FiExternalLink, FiMapPin } from 'react-icons/fi';
import { useLanguage } from '../../i18n';

/** The platforms the dashboard offers, in display order. */
export const SOCIALS = [
  { key: 'facebook', label: 'Facebook', color: '#1877f2', icon: <FaFacebook /> },
  { key: 'instagram', label: 'Instagram', color: '#e4405f', icon: <FaInstagram /> },
  { key: 'tiktok', label: 'TikTok', color: '#111827', icon: <FaTiktok /> },
  { key: 'youtube', label: 'YouTube', color: '#ff0000', icon: <FaYoutube /> },
  { key: 'telegram', label: 'Telegram', color: '#0088cc', icon: <FaTelegramPlane /> },
  { key: 'whatsapp', label: 'WhatsApp', color: '#25d366', icon: <FaWhatsapp /> },
  { key: 'twitter', label: 'X / Twitter', color: '#1da1f2', icon: <FaTwitter /> },
];

const HOSTS = {
  facebook: 'facebook.com',
  instagram: 'instagram.com',
  tiktok: 'tiktok.com',
  youtube: 'youtube.com',
  telegram: 't.me',
  whatsapp: 'wa.me',
  twitter: 'x.com',
};

/**
 * Owners paste very different things (`https://fb.com/x`, `facebook.com/x`,
 * `@username`, a phone number for WhatsApp). Turn each into a usable link.
 */
export function socialHref(key, value) {
  const v = String(value || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;                                  // already a URL
  const base = HOSTS[key];
  const handle = v.replace(/^@/, '');
  if (v.startsWith('@')) return base ? `https://${base}/${handle}` : '';  // an @handle
  if (key === 'whatsapp' && /^[+\d][\d\s\-()]{5,}$/.test(v)) {
    return `https://wa.me/${v.replace(/\D/g, '')}`;                       // handled as a number
  }
  if (v.includes('.') || v.includes('/')) return `https://${v.replace(/^\/+/, '')}`;
  return base ? `https://${base}/${handle}` : '';                         // a bare username
}

export default function ShopSocialLinks({
  social, contact, compact = false, title, className = '',
}) {
  const { t } = useLanguage();
  const links = SOCIALS
    .map((s) => ({ ...s, href: socialHref(s.key, social?.[s.key]) }))
    .filter((s) => s.href);
  const address = String(contact || '').trim();

  if (links.length === 0 && !address) return null;

  if (compact) {
    return (
      <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
        {links.map((s) => (
          <a
            key={s.key}
            href={s.href}
            target="_blank"
            rel="noreferrer"
            title={s.label}
            aria-label={s.label}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:scale-110 transition"
            style={{ background: s.color }}
          >
            {s.icon}
          </a>
        ))}
        {address && (
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400 max-w-[220px] truncate">
            <FiMapPin className="w-3.5 h-3.5 shrink-0" /> {address}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {links.length > 0 && (
        <>
          <p className="text-xs font-semibold text-gray-500 mb-1.5">{title || t('followUs')}</p>
          <div className="flex flex-wrap gap-2">
            {links.map((s) => (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border hover:brightness-95 transition"
                style={{ color: s.color, borderColor: `${s.color}55`, background: `${s.color}12` }}
              >
                {s.icon} {s.label} <FiExternalLink className="w-3 h-3 opacity-70" />
              </a>
            ))}
          </div>
        </>
      )}
      {address && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <FiMapPin className="w-3.5 h-3.5" /> {address}
        </p>
      )}
    </div>
  );
}
